const WALLETS_URL =
  'https://opensheet.elk.sh/1-CK2SWbwwgqx6DVLjRDBY7FaUWhz9Tp3_gKWAWACC48/1';
const TIER_ALLOCATION_URL =
  'https://opensheet.elk.sh/1-CK2SWbwwgqx6DVLjRDBY7FaUWhz9Tp3_gKWAWACC48/2';

const CACHE_TTL_MS = 5 * 60 * 1000; // Allow edits to propagate without hammering the sheet.

let cachedDataset = null;
let inflightPromise = null;
let lastFetchedAt = 0;

export function normalizeAddress(address) {
  if (!address) return '';
  return String(address).trim().toLowerCase();
}

export function isMaybeAddress(address) {
  const a = normalizeAddress(address);
  return /^0x[a-f0-9]{40}$/.test(a);
}

export function getCachedAllocationForAddress(address) {
  if (!cachedDataset) return null;
  const key = normalizeAddress(address);
  if (!isMaybeAddress(key)) return null;
  return cachedDataset.allocationsByAddress[key] ?? null;
}

export function getCachedAddresses() {
  return cachedDataset?.addresses ?? [];
}

export async function loadAllocationsData({ forceRefresh = false } = {}) {
  const now = Date.now();
  if (!forceRefresh && cachedDataset && now - lastFetchedAt < CACHE_TTL_MS) {
    return cachedDataset;
  }

  if (!forceRefresh && inflightPromise) {
    return inflightPromise;
  }

  inflightPromise = fetchAllocationsDataset()
    .then((dataset) => {
      cachedDataset = dataset;
      lastFetchedAt = Date.now();
      return cachedDataset;
    })
    .finally(() => {
      inflightPromise = null;
    });

  return inflightPromise;
}

async function fetchAllocationsDataset() {
  const [walletRows, tierRows] = await Promise.all([
    fetchJson(WALLETS_URL),
    fetchJson(TIER_ALLOCATION_URL),
  ]);

  const tierAllocations = buildTierAllocationMap(tierRows);
  const { allocationsByAddress, addresses } = buildWalletAllocationMap(
    walletRows,
    tierAllocations,
  );

  if (!addresses.length) {
    throw new Error('No wallet allocations received from sheet');
  }

  return {
    allocationsByAddress,
    addresses,
    tierAllocations,
  };
}

async function fetchJson(url) {
  const response = await fetch(url);
  if (!response.ok) {
    const error = new Error(`Failed to fetch allocations from ${url}`);
    error.status = response.status;
    throw error;
  }
  return response.json();
}

function buildTierAllocationMap(rows = []) {
  return rows.reduce((acc, row) => {
    const tier = sanitizeTier(row?.Tier);
    if (!tier) return acc;

    acc[tier] = {
      claim: parseAmount(row?.['Airdrop Allocation']),
      presale: parseAmount(row?.['Presale Allocation']),
      tierName: row?.['Tier Name'] ?? 'Bob-tom Feeder',
    };

    return acc;
  }, {});
}

function buildWalletAllocationMap(rows = [], tierAllocations = {}) {
  const allocationsByAddress = {};
  const addresses = [];

  rows.forEach((row) => {
    const address = normalizeAddress(row?.['Wallet Address']);
    if (!isMaybeAddress(address)) return;

    const tier = sanitizeTier(row?.Tier);
    if (!tier) return;

    if (!allocationsByAddress[address]) {
      addresses.push(address);
    }

    const tierAllocation =
      tierAllocations[tier] ?? { claim: 0, presale: 0, tierName: 'Bob-tom Feeder' };

    allocationsByAddress[address] = {
      tier,
      tierName: tierAllocation.tierName,
      claim: tierAllocation.claim,
      presale: tierAllocation.presale,
    };
  });

  return { allocationsByAddress, addresses };
}

function sanitizeTier(rawTier) {
  if (rawTier === undefined || rawTier === null) return null;
  const tier = String(rawTier).trim();
  return tier ? tier : null;
}

function parseAmount(value) {
  if (value === undefined || value === null || value === '') return 0;
  const parsed = Number(String(value).replace(/,/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
}
